import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';
import { snapshot, dustRun } from './studio-hero-renderer';
import { visibleRatio } from '../../ui/visible-ratio';
import { reduced } from '../../ui/motion';

/** Смена только представления: опубликованные действия и данные остаются в React.
    Порядок — goSlide эталона (bot-studio.js): номер кадра и точки меняются сразу (slide); снимок берётся с текущего
    кадра, пока он стоит как есть (только on); затем над ним встаёт холст частиц, новый кадр получает on in, старый
    теряет on (shown, entering) — в одном кадре с холстом; in снимается, когда частицы догорели. Без частиц — меньше
    движения, заморозка .still, слайдер за краем, снимок не удался — классы меняются сразу и без in. */
export function useStudioHeroTransition(root: RefObject<HTMLElement | null>, count: number, initial = 0) {
  const [slide, setSlide] = useState(initial);
  const [view, setView] = useState({ shown: initial, entering: false });
  /** Последний запрошенный кадр (S.i эталона): от него считает следующий шаг автопрокрутки */
  const requested = useRef(initial);
  const current = useRef(initial);
  const pending = useRef<number | null>(null);
  const busy = useRef(false);
  const lifetime = useRef<AbortController | null>(null);
  const ownedCanvases = useRef(new Set<HTMLCanvasElement>());
  useEffect(() => {
    const controller = new AbortController();
    const canvases = ownedCanvases.current;
    lifetime.current = controller;
    return () => {
      controller.abort();
      canvases.forEach(canvas => canvas.remove());
      canvases.clear();
    };
  }, [root]);
  const changeSlide = useCallback(
    (index: number) => {
      if (!count) return;
      const next = ((index % count) + count) % count;
      requested.current = next;
      setSlide(next);
      pending.current = next;
      if (busy.current) return;
      busy.current = true;
      const signal = lifetime.current?.signal;
      void (async () => {
        try {
          while (pending.current !== null && !signal?.aborted) {
            const target = pending.current;
            pending.current = null;
            if (target === current.current) continue;
            const track = root.current?.querySelector<HTMLElement>('.st-trk');
            const old = track?.querySelector<HTMLElement>('.st-hs.on');
            const width = track?.clientWidth ?? 0;
            const height = track?.clientHeight ?? 0;
            let pixels: HTMLCanvasElement | null = null;
            const dpr = Math.min(devicePixelRatio || 1, 2);
            if (track && old && width && height && !reduced() && !document.hidden && visibleRatio(track) >= 0.5) {
              try {
                pixels = await snapshot(old, width, height, dpr);
              } catch {
                /* Недоступный canvas: обычная смена. */
              }
            }
            if (signal?.aborted) break;
            current.current = target;
            if (!pixels || !track?.isConnected) {
              setView({ shown: target, entering: false });
              continue;
            }
            const canvas = document.createElement('canvas');
            canvas.className = 'st-dustcv';
            canvas.setAttribute('aria-hidden', 'true');
            track.append(canvas);
            ownedCanvases.current.add(canvas);
            // холст и смена кадров — в одном кадре, как у эталона: между ними нет кадра со старым видом без холста
            flushSync(() => setView({ shown: target, entering: true }));
            let timeout: number | undefined;
            try {
              await Promise.race([
                dustRun(canvas, pixels, width, height, dpr),
                new Promise<void>(resolve => {
                  timeout = window.setTimeout(resolve, 3000);
                }),
              ]);
            } catch {
              /* Например, внешняя картинка запретила чтение пикселей. */
            } finally {
              clearTimeout(timeout);
              canvas.remove();
              ownedCanvases.current.delete(canvas);
            }
            // in снимается до следующей смены из очереди: её снимок — с кадра, стоящего как есть
            if (!signal?.aborted) flushSync(() => setView({ shown: target, entering: false }));
          }
        } finally {
          busy.current = false;
        }
      })();
    },
    [count, root],
  );
  /** Шаг от последнего запрошенного кадра (next/prev эталона) */
  const shift = useCallback((delta: number) => changeSlide(requested.current + delta), [changeSlide]);
  return { slide, shown: view.shown, entering: view.entering, changeSlide, shift };
}
